import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = import.meta.env.MODE;
// Enable in production AND preview (lovable.app hosts) so we capture issues users report from preview
const isPreviewHost =
  typeof window !== 'undefined' && /\.lovable\.(app|dev)$/.test(window.location.hostname);
const ENABLED = !!DSN && (ENV === 'production' || isPreviewHost);

// Known browser-extension / translator attribute names that mutate the DOM
// and frequently cause React `removeChild`/`NotFoundError` crashes.
const SUSPICIOUS_HTML_ATTRS = [
  'translated-ltr',
  'translated-rtl',
  'lt-installed', // LanguageTool
  'data-lt-installed',
  'cz-shortcut-listen', // ColorZilla
  'data-grammarly-shadow-root',
  'spellcheck-extension',
];

interface EnvFlags {
  translated: boolean;
  translatorClass: boolean;
  suspiciousAttrs: string[];
  injectedScripts: string[];
  bodyExtensionAttrs: string[];
}

function detectEnvFlags(): EnvFlags {
  const html = document.documentElement;
  const attrs = html.getAttributeNames();
  const suspicious = attrs.filter(a => SUSPICIOUS_HTML_ATTRS.includes(a));
  const translated =
    suspicious.some(a => a.startsWith('translated-')) ||
    html.classList.contains('translated-ltr') ||
    html.classList.contains('translated-rtl') ||
    !!document.querySelector('html[class*="translated-"]') ||
    !!document.querySelector('font[style*="vertical-align"]'); // GTrans wraps text in <font>

  const injected = Array.from(document.querySelectorAll('script[src]'))
    .map(s => (s as HTMLScriptElement).src)
    .filter(src => /chrome-extension:|moz-extension:|safari-extension:/.test(src));

  const bodyAttrs = document.body
    ? document.body.getAttributeNames().filter(a => /^(data-)?(grammarly|lt|cz|gr-|gtx)/i.test(a))
    : [];

  return {
    translated,
    translatorClass:
      html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl'),
    suspiciousAttrs: suspicious,
    injectedScripts: injected,
    bodyExtensionAttrs: bodyAttrs,
  };
}

let lastFlags: EnvFlags | null = null;

function startExtensionWatcher() {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;

  const fire = (reason: string) => {
    const flags = detectEnvFlags();
    lastFlags = flags;
    Sentry.addBreadcrumb({
      category: 'extension',
      level: 'warning',
      message: `DOM mutation detected: ${reason}`,
      data: flags as unknown as Record<string, unknown>,
    });
    Sentry.setTag('translated_page', String(flags.translated));
    Sentry.setTag('extension_attrs_present', String(flags.suspiciousAttrs.length > 0));
  };

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName) {
        if (
          SUSPICIOUS_HTML_ATTRS.includes(m.attributeName) ||
          m.attributeName.startsWith('translated-') ||
          /grammarly|^lt-|^cz-|gtx/i.test(m.attributeName)
        ) {
          fire(`<${(m.target as Element).tagName.toLowerCase()} ${m.attributeName}>`);
          return;
        }
      }
    }
  });

  // Watch <html> and <body> for attribute mutations from extensions/translators
  observer.observe(document.documentElement, { attributes: true });
  if (document.body) observer.observe(document.body, { attributes: true });
  else
    document.addEventListener(
      'DOMContentLoaded',
      () => observer.observe(document.body, { attributes: true }),
      { once: true }
    );

  // Initial snapshot tag (so every event has it even without mutations)
  lastFlags = detectEnvFlags();
  Sentry.setTag('translated_page', String(lastFlags.translated));
  Sentry.setTag('extension_attrs_present', String(lastFlags.suspiciousAttrs.length > 0));
}

export function initSentry() {
  if (!ENABLED) return;

  Sentry.init({
    dsn: DSN,
    environment: isPreviewHost && ENV !== 'production' ? 'preview' : ENV,
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    dist: (import.meta.env.VITE_BUILD_HASH as string | undefined) || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay integration wyłączona — wyczerpany limit 50 nagrań/mc na darmowym planie Sentry.
      // Aby ponownie włączyć: przywróć Sentry.replayIntegration({...}) i ustaw replaysOnErrorSampleRate > 0.
    ],
    tracesSampleRate: ENV === 'production' ? 0.05 : 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Stale-chunk after deploy — handled by chunkReload.ts (auto-reload)
      'Importing a module script failed',
      'Failed to fetch dynamically imported module',
      'error loading dynamically imported module',
      'Unable to preload CSS',
    ],
    sendDefaultPii: false,
    beforeSend(event, hint) {
      try {
        const flags = lastFlags ?? detectEnvFlags();
        event.tags = {
          ...event.tags,
          translated_page: String(flags.translated),
          extension_attrs_present: String(flags.suspiciousAttrs.length > 0),
          route: window.location.pathname,
        };
        event.contexts = {
          ...event.contexts,
          environment_flags: {
            translated: flags.translated,
            suspicious_html_attrs: flags.suspiciousAttrs,
            injected_extension_scripts: flags.injectedScripts,
            body_extension_attrs: flags.bodyExtensionAttrs,
            url: window.location.href,
            user_agent: navigator.userAgent,
            language: navigator.language,
          },
        };
        const err = hint?.originalException as Error | undefined;
        const msg = err?.message || (typeof event.message === 'string' ? event.message : '');

        // Drop Safari/network-abort noise from supabase RPC + edge fn instrumentation.
        // These are user-side connectivity issues (tab backgrounded, network drop,
        // page reload mid-request) — not actionable bugs.
        const extraErrMsg = String(
          (event.extra as { error_message?: unknown } | undefined)?.error_message ?? ''
        );
        const networkNoise =
          /TypeError: Load failed/i.test(extraErrMsg) ||
          /TypeError: Load failed/i.test(msg) ||
          /Failed to fetch/i.test(extraErrMsg) ||
          /Failed to fetch/i.test(msg) ||
          /NetworkError when attempting to fetch/i.test(msg) ||
          /The operation was aborted/i.test(msg) ||
          /AbortError/i.test(msg) ||
          /The network connection was lost/i.test(msg);

        const isSupabaseArea = (event.tags as { area?: string } | undefined)?.area === 'supabase';

        if (networkNoise && isSupabaseArea) {
          return null; // drop event
        }

        // Drop stale-chunk import errors that slip past ignoreErrors string match
        if (
          /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS/i.test(
            msg
          )
        ) {
          return null;
        }

        // Mark likely-extension-induced React DOM crashes
        if (
          flags.translated &&
          /removeChild|insertBefore|NotFoundError|Failed to execute 'removeChild'/.test(msg)
        ) {
          event.tags.likely_translator_crash = 'true';
          event.fingerprint = ['translator-domremoval', msg.slice(0, 80)];
        }
      } catch {
        /* never block sending */
      }
      return event;
    },
  });

  startExtensionWatcher();
}

export function setSentryUser(user: { id: string; email?: string | null } | null) {
  if (!ENABLED) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
}

export { Sentry };
